// ============================================================================
// DineOpsBillRegisterScreen — Restaurant → Reports → Bill Register (RPT-008, S2)
// ============================================================================
// Every bill of a period with the columns of the legacy POS bill register the
// auditors work from: outlet, bill, date, time, customer, GSTIN (B2B / B2C),
// item total, discount, service charge, taxable, CGST, SGST, round-off, total,
// each settlement method, pax and status. The working outlet, or every outlet
// the user may use. By month gives the month totals per outlet. Excel and the
// PDF are built by the API (the same files as the Day Close summary's Bills).
// Design: crs-doc DineOps/DineOps-Counters-Shifts-and-Day-Close.md section 7.1.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell } from 'components/enterprise-shell';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { getActiveDineOutletName } from 'auth/dineOutlet';
import type { DayAuditBill, DineBillRegister } from 'api/types';
import { reserveTab, saveReportFile, showPdf, blobErrorMessage } from 'utils/reportFile';

const isoOf = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const todayIso = () => isoOf(new Date());
const monthStartIso = () => { const d = new Date(); d.setDate(1); return isoOf(d); };
const formatDate = (iso?: string | null) =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const money = (n?: number | null) => (n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const right = (v: React.ReactNode, strong = false) =>
  <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: strong ? 700 : 400 }}>{v}</span>;
const taxable = (b: DayAuditBill) => b.totalAmount - b.taxAmount - (b.roundOffAmount ?? 0);

const statusOf = (b: DayAuditBill) => {
  if (b.billStatus === 'VOID') return 'Void';
  if (b.billStatus === 'GENERATED') return 'Open';
  if (b.settlementType === 'COMPLIMENTARY') return 'Complimentary';
  if (b.settlementType === 'ROOM_CHARGE') return 'Settled · to room';
  if (b.settlementType === 'MIXED') return 'Settled · paid + room';
  return 'Settled';
};

interface Props {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  /** RBAC: {'view','export'}. Fail-closed: [] renders no export. */
  allowedActions?: string[];
}

type Row = DayAuditBill & { rowId: string };
interface MonthRow { id: string; month: string; outlet: string; bills: number; subtotal: number; discount: number; taxable: number; cgst: number; sgst: number; total: number; cash: number; card: number; upi: number; wallet: number; room: number }

const DineOpsBillRegisterScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const canExport = allowedActions.includes('export');
  const outletName = useMemo(() => getActiveDineOutletName() || 'this outlet', []);
  const [from, setFrom] = useState(monthStartIso());
  const [to, setTo] = useState(todayIso());
  const [allOutlets, setAllOutlets] = useState(false);
  const [view, setView] = useState<'bills' | 'months'>('bills');
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const enabled = !!from && !!to && from <= to;
  const [report, setReport] = useState<DineBillRegister | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!enabled) { setReport(null); return; }
    setLoading(true);
    setError(null);
    try { setReport(await dineOpsService.getBillRegister({ from, to, allOutlets })); }
    catch (err: any) { setReport(null); setError(err?.response?.data?.message || err?.message || 'The report did not load.'); }
    finally { setLoading(false); }
  }, [enabled, from, to, allOutlets]);
  useEffect(() => { void load(); }, [load]);

  const q = searchQuery.trim().toLowerCase();
  const rows = useMemo<Row[]>(() => (report?.bills ?? [])
    .filter((b) => !q || `${b.billNumber} ${b.customerName ?? ''} ${b.customerGstin ?? ''} ${b.outletName ?? ''} ${b.roomNumber ?? ''}`.toLowerCase().includes(q))
    .map((b) => ({ ...b, rowId: b.id })), [report, q]);
  const live = useMemo(() => (report?.bills ?? []).filter((b) => b.billStatus !== 'VOID'), [report]);

  const months = useMemo<MonthRow[]>(() => {
    const m = new Map<string, MonthRow>();
    for (const b of live) {
      const month = (b.businessDate ?? '').slice(0, 7);
      const key = `${month}|${b.outletName ?? ''}`;
      const r = m.get(key) ?? { id: key, month, outlet: b.outletName ?? '', bills: 0, subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, total: 0, cash: 0, card: 0, upi: 0, wallet: 0, room: 0 };
      r.bills += 1; r.subtotal += b.subtotal; r.discount += b.discountAmount; r.taxable += taxable(b); r.cgst += b.cgst ?? 0; r.sgst += b.sgst ?? 0;
      r.total += b.totalAmount; r.cash += b.cashAmount ?? 0; r.card += b.cardAmount ?? 0; r.upi += b.upiAmount ?? 0; r.wallet += b.walletAmount ?? 0; r.room += b.roomChargeAmount ?? 0;
      m.set(key, r);
    }
    return [...m.values()].sort((a, b) => a.month.localeCompare(b.month) || a.outlet.localeCompare(b.outlet));
  }, [live]);

  const columns = useMemo<SyutGridColumn<Row>[]>(() => [
    { id: 'date', header: 'Date', accessorKey: 'businessDate', size: 110, enableSorting: true, cell: ({ row }) => formatDate(row.original.businessDate) },
    { id: 'outlet', header: 'Outlet', accessorKey: 'outletName', size: 140, enableSorting: true },
    { id: 'bill', header: 'Bill', accessorKey: 'billNumber', size: 170, enableSorting: true, cell: ({ row }) => <strong>{row.original.billNumber}</strong> },
    { id: 'time', header: 'Time', accessorKey: 'generatedAt', size: 80, cell: ({ row }) => new Date(row.original.generatedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) },
    { id: 'customer', header: 'Customer', accessorKey: 'customerName', size: 180, cell: ({ row }) => row.original.customerName ?? (row.original.customerType === 'IN_HOUSE' ? 'In-house guest' : 'Walk-in') },
    { id: 'gstin', header: 'GSTIN', accessorKey: 'customerGstin', size: 150, cell: ({ row }) => row.original.customerGstin ?? '—' },
    { id: 'b2b', header: 'B2B / B2C', accessorKey: 'customerGstin', size: 80, cell: ({ row }) => (row.original.customerGstin ? 'B2B' : 'B2C') },
    { id: 'subtotal', header: 'Item total', accessorKey: 'subtotal', size: 110, cell: ({ row }) => right(money(row.original.subtotal)) },
    { id: 'discount', header: 'Discount', accessorKey: 'discountAmount', size: 100, cell: ({ row }) => right(money(row.original.discountAmount)) },
    { id: 'taxable', header: 'Taxable', accessorKey: 'subtotal', size: 110, cell: ({ row }) => right(money(taxable(row.original))) },
    { id: 'cgst', header: 'CGST', accessorKey: 'cgst', size: 90, cell: ({ row }) => right(money(row.original.cgst)) },
    { id: 'sgst', header: 'SGST', accessorKey: 'sgst', size: 90, cell: ({ row }) => right(money(row.original.sgst)) },
    { id: 'total', header: 'Total', accessorKey: 'totalAmount', size: 110, enableSorting: true, cell: ({ row }) => right(money(row.original.totalAmount), true) },
    { id: 'cash', header: 'Cash', accessorKey: 'cashAmount', size: 100, cell: ({ row }) => right(money(row.original.cashAmount)) },
    { id: 'card', header: 'Card', accessorKey: 'cardAmount', size: 100, cell: ({ row }) => right(money(row.original.cardAmount)) },
    { id: 'upi', header: 'UPI', accessorKey: 'upiAmount', size: 100, cell: ({ row }) => right(money(row.original.upiAmount)) },
    { id: 'wallet', header: 'Wallet', accessorKey: 'walletAmount', size: 90, cell: ({ row }) => right(money(row.original.walletAmount)) },
    { id: 'room', header: 'To room', accessorKey: 'roomChargeAmount', size: 100, cell: ({ row }) => right(money(row.original.roomChargeAmount)) },
    { id: 'pax', header: 'Pax', accessorKey: 'covers', size: 60, cell: ({ row }) => right(row.original.covers ?? 0) },
    { id: 'status', header: 'Status', accessorKey: 'billStatus', size: 150, enableSorting: true,
      cell: ({ row }) => <span style={row.original.billStatus === 'VOID' ? { fontStyle: 'italic', color: 'var(--text-muted)' } : undefined}>{statusOf(row.original)}</span> },
  ], []);

  const monthColumns = useMemo<SyutGridColumn<MonthRow>[]>(() => [
    { id: 'month', header: 'Month', accessorKey: 'month', size: 100, cell: ({ row }) => new Date(`${row.original.month}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) },
    { id: 'outlet', header: 'Outlet', accessorKey: 'outlet', size: 150 },
    { id: 'bills', header: 'Bills', accessorKey: 'bills', size: 80, cell: ({ row }) => right(row.original.bills) },
    { id: 'subtotal', header: 'Item total', accessorKey: 'subtotal', size: 120, cell: ({ row }) => right(money(row.original.subtotal)) },
    { id: 'discount', header: 'Discount', accessorKey: 'discount', size: 110, cell: ({ row }) => right(money(row.original.discount)) },
    { id: 'taxable', header: 'Taxable', accessorKey: 'taxable', size: 120, cell: ({ row }) => right(money(row.original.taxable)) },
    { id: 'cgst', header: 'CGST', accessorKey: 'cgst', size: 100, cell: ({ row }) => right(money(row.original.cgst)) },
    { id: 'sgst', header: 'SGST', accessorKey: 'sgst', size: 100, cell: ({ row }) => right(money(row.original.sgst)) },
    { id: 'total', header: 'Total', accessorKey: 'total', size: 120, cell: ({ row }) => right(money(row.original.total), true) },
    { id: 'cash', header: 'Cash', accessorKey: 'cash', size: 110, cell: ({ row }) => right(money(row.original.cash)) },
    { id: 'card', header: 'Card', accessorKey: 'card', size: 110, cell: ({ row }) => right(money(row.original.card)) },
    { id: 'upi', header: 'UPI', accessorKey: 'upi', size: 110, cell: ({ row }) => right(money(row.original.upi)) },
    { id: 'wallet', header: 'Wallet', accessorKey: 'wallet', size: 100, cell: ({ row }) => right(money(row.original.wallet)) },
    { id: 'room', header: 'To room', accessorKey: 'room', size: 110, cell: ({ row }) => right(money(row.original.room)) },
  ], []);

  const [exporting, setExporting] = useState(false);
  const exportFile = useCallback(async (format: 'xlsx' | 'pdf') => {
    if (!enabled) return;
    const tabRef = format === 'pdf' ? reserveTab() : null;
    setExporting(true);
    try {
      const { blob, disposition } = await dineOpsService.exportBillRegister({ from, to, allOutlets }, format);
      if (format === 'pdf') showPdf(blob, tabRef);
      else saveReportFile(blob, disposition, `BillRegister_${from}_${to}.xlsx`);
    } catch (err) {
      tabRef?.close();
      notificationService.toast.error(await blobErrorMessage(err, format === 'pdf' ? 'Print failed.' : 'Export failed.'));
    } finally { setExporting(false); }
  }, [enabled, from, to, allOutlets]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [
      { id: 'view', icon: <IconSystem.list size={16} />, label: view === 'bills' ? 'Show by month' : 'Show every bill',
        onClick: () => setView((v) => (v === 'bills' ? 'months' : 'bills')), variant: 'secondary' },
      { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => { void load(); }, variant: 'icon' },
    ];
    if (canExport) {
      b.push({ id: 'export', icon: <IconSystem.download size={16} />, label: 'Excel', tooltip: 'Bill Register in Excel', onClick: () => void exportFile('xlsx'), disabled: exporting || !enabled, variant: 'secondary' });
      b.push({ id: 'print', icon: <IconSystem.print size={16} />, label: 'Print', tooltip: 'Bill Register as a PDF to print', onClick: () => void exportFile('pdf'), disabled: exporting || !enabled, variant: 'secondary' });
    }
    return b;
  }, [view, load, canExport, exportFile, exporting, enabled]);

  const total = live.reduce((n, b) => n + b.totalAmount, 0);
  const headerItems: ReportHeaderItem[] = [
    { label: 'Period', value: `${formatDate(from)} – ${formatDate(to)}`, primary: true, title: 'Business date of the bill' },
    { label: 'Outlet', value: allOutlets ? 'Every outlet' : outletName, icon: 'home', primary: true },
    ...(report ? [
      { label: 'Bills', value: live.length, title: 'Not counting void bills' },
      { label: 'Void', value: (report.bills.length - live.length) },
      { label: 'Pax', value: live.reduce((n, b) => n + (b.covers ?? 0), 0), icon: 'users' as const },
      { label: 'Bill value', value: money(total), icon: 'creditCard' as const },
    ] : []),
  ];
  const appliedCount = (from !== monthStartIso() || to !== todayIso() ? 1 : 0) + (allOutlets ? 1 : 0);

  return (
    <EnterpriseScreenShell
      title="Bill Register"
      description="Every bill of a period: the register auditors and accounts work from"
      icon={<IconSystem.list size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search bill, customer, GSTIN or room' }}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={allOutlets} onChange={(e) => setAllOutlets(e.target.checked)} />
                  Every outlet I may use (else {outletName})
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setFrom(monthStartIso()); setTo(todayIso()); setAllOutlets(false); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Close</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody footer={report ? <span>Void bills are shown in italics and left out of the figures above. B2B is a bill with the customer&rsquo;s GSTIN. At most a year at a time.</span> : undefined}>
        {!enabled ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Pick a period in the filters.</div>
        ) : error ? (
          <div style={{ color: 'var(--danger, #ef4444)', padding: '2rem', textAlign: 'center' }}>{error}</div>
        ) : view === 'bills' ? (
          <SyutGrid data={rows} columns={columns} isLoading={loading} enableSorting enablePagination defaultPageSize={50} pageSizeOptions={[25, 50, 100, 200]}
            emptyMessage="No bills in the period." />
        ) : (
          <SyutGrid data={months} columns={monthColumns} isLoading={loading} emptyMessage="No bills in the period." />
        )}
      </ReportBody>
    </EnterpriseScreenShell>
  );
};

export default DineOpsBillRegisterScreen;
